import React from 'react';
import { ArrowDown } from '@fabric-ui/icons';
import { Box, BoxProps, Skeleton, SxProps, Theme, useTheme } from '@mui/material';
import { sanitize } from '@utils/utils';
import DOMPurify from 'dompurify';

interface FoundCountResultsProps {
  label: string;
  count?: number;
  height?: number;
  loading?: boolean;
  style?: SxProps<Theme>;
}

export const FoundCountResults = ({ label, count = 0, height = 21, loading, style }: FoundCountResultsProps): React.JSX.Element =>
  loading ? (
    <Skeleton height={height} width={160} />
  ) : (
    <Box sx={{ ...style }}>
      <strong>{count.toLocaleString()}</strong> {label} found
    </Box>
  );

interface CollapseArrowProps {
  isExpanded?: boolean;
}

export function CollapseArrow({ isExpanded = false }: CollapseArrowProps): React.JSX.Element {
  const theme = useTheme();
  return (
    <ArrowDown
      style={{
        transform: `rotate(${isExpanded ? 0 : -90}deg)`,
        transition: theme.transitions.create(['transform'], {
          duration: theme.transitions.duration.shorter,
        }),
      }}
    />
  );
}

interface SanitizeHTMLProps extends BoxProps {
  html: string;
  openLinksInNewTab?: boolean;
}

const styleHTML = {
  'pre, code': {
    backgroundColor: 'var(--color-surface-base-tertiary)',
    color: 'inherit',
  },
  pre: {
    whiteSpace: 'pre-wrap',
    my: '5px',
    padding: '5px 10px',
  },
  code: {
    fontSize: 13,
    padding: '2px 4px',
  },
  a: {
    color: ({ palette }) => palette.primary.main,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
};

const linkSanitizer = DOMPurify(window);
linkSanitizer.addHook('afterSanitizeAttributes', node => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});
const sanitizeWithOpenLinks = (html: string) => linkSanitizer.sanitize(html, { ADD_ATTR: ['target'] });

export function SanitizeHTML({ html, openLinksInNewTab, ...props }: SanitizeHTMLProps): React.JSX.Element {
  const sanitized = openLinksInNewTab ? sanitizeWithOpenLinks(html) : sanitize(html);
  return <Box sx={styleHTML} dangerouslySetInnerHTML={{ __html: sanitized }} {...props} />;
}
